/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1653177600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Sun May 22 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "microsoft-excel-source",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Microsoft Excel formatting requirements",
          "depth": 0,
          "id": "microsoft-excel-formatting-requirements"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Authorize connection",
          "depth": 1,
          "id": "authorize-connection"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Model setup",
          "depth": 1,
          "id": "model-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Need admin approval",
          "depth": 1,
          "id": "need-admin-approval"
        }, {
          "value": "Files not appearing in model dropdown",
          "depth": 1,
          "id": "files-not-appearing-in-model-dropdown"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      a: "a",
      img: "img",
      strong: "strong",
      h3: "h3",
      ol: "ol"
    }, _provideComponents(), props.components), {CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in Microsoft Excel and push it to downstream destinations. Before connecting your Microsoft Excel source, it's best to make sure they meet the formatting requirements."
      }), "\n", _jsx(_components.h2, {
        id: "microsoft-excel-formatting-requirements",
        children: "Microsoft Excel formatting requirements"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Your Microsoft Excel workbook must be a ", _jsx(_components.code, {
            children: ".xlsx"
          }), " file. Other extension workbooks, for example, ", _jsx(_components.code, {
            children: ".xls"
          }), " or ", _jsx(_components.code, {
            children: ".csv"
          }), " aren't supported. ", _jsx(_components.a, {
            href: "https://answers.microsoft.com/en-us/msoffice/forum/all/converting-xls-to-xlsx/ba8d05f4-d38a-462a-a062-d6083db4b479",
            children: "Convert your file"
          }), " if necessary."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Excel worksheet must have column names in the first row. In the following image, you can see that the column names are ", _jsx(_components.code, {
            children: "Country"
          }), ", ", _jsx(_components.code, {
            children: "Product"
          }), ", ", _jsx(_components.code, {
            children: "Discount Band"
          }), ", ", _jsx(_components.code, {
            children: "Units Sold"
          }), ", and ", _jsx(_components.code, {
            children: " Manufacturing Price"
          }), " etc."]
        }), "\n", _jsx(_components.li, {
          children: "To ensure accurate processing, all valid data must be present in the top-left portion of the sheet. If a column name is left empty, the preceding column will be treated as the last column in the file. Similarly, if a row is left empty, the preceding row will be considered the last row in the file, and any subsequent rows will not be processed."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/source-microsoft-excel.png",
          alt: "Microsoft Excel Example"
        })
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Microsoft Excel"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "authorize-connection",
        children: "Authorize connection"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Log in to Microsoft Excel"
          }), " and select the desired Microsoft Account."]
        }), "\n", _jsxs(_components.li, {
          children: ["After reading what authorization you're giving to Hightouch, click ", _jsx(_components.strong, {
            children: "Accept"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Drive"
          }), " containg the data you wish to sync from the dropdown."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up Microsoft Excel as a source for the first time, Hightouch checks that you have the correct permissions. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Microsoft Excel."]
      }), "\n", _jsx(_components.h3, {
        id: "model-setup",
        children: "Model setup"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add model."
          })]
        }), "\n", _jsx(_components.li, {
          children: "Select the Microsoft Excel source you previously created."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Workbook"
          }), " which has the data you want to sync."]
        }), "\n", _jsxs(_components.li, {
          children: ["Next, select a ", _jsx(_components.strong, {
            children: "Sheet"
          }), " to read from. Hightouch treats each sheet as a separate table. If you want to sync data from multiple sheets, you need to set up a separate model for each one."]
        }), "\n", _jsx(_components.li, {
          children: "Preview your model's query results."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Name your model and select its ", _jsx(_components.a, {
            href: "/getting-started/concepts#primary-key-requirement",
            children: _jsx(_components.strong, {
              children: "primary key"
            })
          }), ". Hightouch uses the primary key to determine which rows have been added, changed, or removed since the last sync."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "need-admin-approval",
        children: "Need admin approval"
      }), "\n", _jsxs(_components.p, {
        children: ["If you see a ", _jsx(_components.strong, {
          children: "Need admin approval"
        }), " page appear when ", _jsx(_components.a, {
          href: "#connect-to-microsoft-onedrive",
          children: "accessing Microsoft's OAuth login flow"
        }), ", your Azure Active Directory user account might not have the necessary permissions to connect applications to your organization."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspaces/permissions/microsoft-need-admin-approval.png",
          alt: "Microsoft's \"Need admin approval\" screen"
        })
      }), "\n", _jsx(_components.p, {
        children: "Ask one of your Azure organization's admins to edit your user account permissions."
      }), "\n", _jsxs(_components.p, {
        children: ["You can read more about ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/active-directory/develop/active-directory-how-applications-are-added#who-has-permission-to-add-applications-to-my-azure-ad-instance",
          children: "Azure user permissions"
        }), " and ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/active-directory/manage-apps/configure-user-consent?pivots=portal#configure-user-consent-settings",
          children: "configuring user settings"
        }), " in Azure's documentation."]
      }), "\n", _jsx(_components.h3, {
        id: "files-not-appearing-in-model-dropdown",
        children: "Files not appearing in model dropdown"
      }), "\n", _jsxs(_components.p, {
        children: ["Only ", _jsx(_components.code, {
          children: ".xlsx"
        }), " files are ", _jsx(_components.a, {
          href: "#microsoft-excel-formatting-requirements",
          children: "supported"
        }), ".\nOther files won't appear in the dropdown.\n", _jsxs(_components.a, {
          href: "https://answers.microsoft.com/en-us/msoffice/forum/all/converting-xls-to-xlsx/ba8d05f4-d38a-462a-a062-d6083db4b479",
          children: ["Convert your file type to ", _jsx(_components.code, {
            children: ".xlsx"
          })]
        }), " to make it available to Highttouch."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
